import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Unified infinite scroll hook with consistent logic across all components
 * Fixes issues: excessive spacing, missing last candidates, inconsistent behavior, scroll position jumping
 * @param {Object} config - Configuration object
 * @param {Function} config.fetchData - Function to fetch data (should return { results, count, hasNext })
 * @param {Array} config.initialData - Initial data array
 * @param {number} config.initialTotalCount - Initial total count
 * @param {number} config.pageSize - Number of items per page (default: 20)
 * @param {Array} config.dependencies - Dependencies that should trigger a reset
 * @param {boolean} config.enabled - Whether infinite scroll is enabled (default: true)
 * @returns {Object} - { data, loading, hasMore, loadMore, reset, error, totalCount, lastElementRef }
 */
const useUnifiedInfiniteScroll = ({
  fetchData,
  initialData = [],
  initialTotalCount = 0,
  pageSize = 20,
  dependencies = [],
  enabled = true,
}) => {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(initialTotalCount);
  const [remainingSearchData, setRemainingSearchData] = useState([]);

  const observerRef = useRef();
  const loadingRef = useRef(false);
  const lastElementRef = useRef();
  const isInitializedRef = useRef(false);
  const scrollPositionRef = useRef(0);
  const scrollDebugRef = useRef(null);

  // Reset function to clear data and start fresh
  const reset = useCallback(() => {
    console.log("🔄 UNIFIED RESET - Resetting infinite scroll...");
    setData([]);
    setCurrentPage(1);
    setHasMore(true);
    setError(null);
    setTotalCount(0);
    setRemainingSearchData([]);
    loadingRef.current = false;
    isInitializedRef.current = false;
    console.log("🔄 UNIFIED RESET - Reset completed");
  }, []);

  // Load more data function with improved logic
  const loadMore = useCallback(async () => {
    console.log("📥 UNIFIED LOAD MORE - Called:", {
      enabled,
      loading: loadingRef.current,
      hasMore,
      fetchData: !!fetchData,
      currentPage,
      pageSize,
      remainingSearchDataLength: remainingSearchData.length,
      dataLength: data.length,
      totalCount: totalCount
    });
    
    if (!enabled || loadingRef.current || !hasMore) {
      console.log("📥 UNIFIED LOAD MORE - Skipping:", {
        enabled,
        loading: loadingRef.current,
        hasMore
      });
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Check if we have remaining search data to load
      if (remainingSearchData.length > 0) {
        console.log("📥 UNIFIED LOAD MORE - Loading from remaining search data...");
        const nextBatch = remainingSearchData.slice(0, pageSize);
        const newRemainingData = remainingSearchData.slice(pageSize);
        
        console.log("📥 UNIFIED LOAD MORE - Next batch:", nextBatch.length, "New remaining:", newRemainingData.length);
        
        // Append the next batch to existing data
        setData(prevData => [...prevData, ...nextBatch]);
        setRemainingSearchData(newRemainingData);
        setHasMore(newRemainingData.length > 0);
        setCurrentPage(prev => prev + 1);
        
        console.log("📥 UNIFIED LOAD MORE - Search data loaded successfully");
        return;
      }
      
      // If no remaining search data, use regular fetchData
      if (!fetchData) {
        console.log("📥 UNIFIED LOAD MORE - No fetchData function available");
        setHasMore(false);
        return;
      }
      
      console.log("📥 UNIFIED LOAD MORE - Calling fetchData...");
      const result = await fetchData(currentPage, pageSize);
      
      if (result) {
        const { results = [], count = 0, hasNext = false } = result;
        
        console.log("📥 UNIFIED LOAD MORE - Result received:", {
          resultsCount: results.length,
          totalCount: count,
          hasNext,
          currentPage,
          resultsIds: results.slice(0, 3).map(r => r.id || r.candidate_id || 'no-id')
        });
        
        if (currentPage === 1) {
          // First page - replace data
          console.log("📥 UNIFIED LOAD MORE - Setting first page data...");
          setData(results);
        } else {
          // Subsequent pages - append data
          console.log("📥 UNIFIED LOAD MORE - Appending data to existing...");
          setData(prevData => {
            const newData = [...prevData, ...results];
            console.log("📥 UNIFIED LOAD MORE - Data after append:", {
              prevDataLength: prevData.length,
              newResultsLength: results.length,
              totalNewLength: newData.length,
              expectedTotal: count,
              missingCount: count - newData.length
            });
            return newData;
          });
        }
        
        setTotalCount(count);
        setHasMore(hasNext);
        setCurrentPage(prev => prev + 1);
        console.log("📥 UNIFIED LOAD MORE - Data updated successfully");
      } else {
        console.log("📥 UNIFIED LOAD MORE - No result received, setting hasMore to false");
        setHasMore(false);
      }
    } catch (err) {
      console.error('❌ UNIFIED LOAD MORE - Error:', err);
      setError(err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [enabled, currentPage, pageSize, hasMore, fetchData, remainingSearchData, data.length]);

  // Load initial data function
  const loadInitial = useCallback(() => {
    console.log("🚀 UNIFIED LOAD INITIAL - Called:", {
      enabled,
      loading: loadingRef.current,
      dataLength: data.length,
      isInitialized: isInitializedRef.current
    });
    
    if (enabled && !loadingRef.current && data.length === 0 && !isInitializedRef.current) {
      console.log("🚀 UNIFIED LOAD INITIAL - Calling loadMore...");
      isInitializedRef.current = true;
      loadMore();
    } else {
      console.log("🚀 UNIFIED LOAD INITIAL - Skipping:", {
        enabled,
        loading: loadingRef.current,
        dataLength: data.length,
        isInitialized: isInitializedRef.current
      });
    }
  }, [enabled, data.length, loadMore]);

  // Set data directly function (for search results) with scroll position preservation
  const setDataDirectly = useCallback((newData, newTotalCount = 0, preserveScroll = true) => {
    console.log("🔍 SCROLL DEBUG - setDataDirectly called with:", {
      newDataLength: newData.length,
      newTotalCount,
      pageSize,
      preserveScroll,
      timestamp: new Date().toISOString()
    });

    // Store current scroll position if preserving scroll
    let currentScrollTop = 0;
    if (preserveScroll) {
      // Get all possible scroll positions for debugging
      const windowScrollY = window.pageYOffset;
      const documentScrollTop = document.documentElement.scrollTop;
      const bodyScrollTop = document.body.scrollTop;
      const windowScrollTop = window.scrollY;

      currentScrollTop = windowScrollY || documentScrollTop || bodyScrollTop || 0;
      scrollPositionRef.current = currentScrollTop;

      console.log("🔍 SCROLL DEBUG - BEFORE data update - All scroll positions:", {
        windowPageYOffset: windowScrollY,
        windowScrollY: windowScrollTop,
        documentElementScrollTop: documentScrollTop,
        bodyScrollTop: bodyScrollTop,
        finalScrollTop: currentScrollTop,
        scrollPositionRef: scrollPositionRef.current
      });
    }

    // Show only the first batch of results
    const firstBatch = newData.slice(0, pageSize);
    const remainingData = newData.slice(pageSize);

    console.log("🔍 SCROLL DEBUG - Data batching:", {
      totalData: newData.length,
      firstBatchLength: firstBatch.length,
      remainingDataLength: remainingData.length,
      pageSize: pageSize
    });

    // Update state
    console.log("🔍 SCROLL DEBUG - About to update state...");
    setData(firstBatch);
    setTotalCount(newTotalCount);
    setCurrentPage(1);
    setHasMore(remainingData.length > 0);
    setError(null);
    loadingRef.current = false;
    isInitializedRef.current = true;
    setRemainingSearchData(remainingData);

    console.log("🔍 SCROLL DEBUG - State updated, scroll position after state update:", {
      windowPageYOffset: window.pageYOffset,
      documentElementScrollTop: document.documentElement.scrollTop,
      bodyScrollTop: document.body.scrollTop
    });

    // Restore scroll position after a brief delay to allow DOM updates
    if (preserveScroll && currentScrollTop > 0) {
      console.log("🔍 SCROLL DEBUG - Starting scroll restoration process...");

      // Use multiple approaches to ensure scroll position is preserved
      const restoreScroll = (attempt) => {
        const targetScrollTop = scrollPositionRef.current;
        const currentScroll = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

        console.log(`🔍 SCROLL DEBUG - Restore attempt ${attempt}:`, {
          targetScrollTop: targetScrollTop,
          currentScrollBeforeRestore: currentScroll,
          difference: Math.abs(currentScroll - targetScrollTop)
        });

        // Method 1: Use window.scrollTo
        window.scrollTo({
          top: targetScrollTop,
          behavior: 'instant'
        });

        // Method 2: Also try document.documentElement.scrollTop as fallback
        if (document.documentElement) {
          document.documentElement.scrollTop = targetScrollTop;
        }

        // Method 3: Try document.body.scrollTop as well
        if (document.body) {
          document.body.scrollTop = targetScrollTop;
        }

        // Check if restoration worked
        setTimeout(() => {
          const finalScroll = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
          console.log(`🔍 SCROLL DEBUG - After restore attempt ${attempt}:`, {
            targetScrollTop: targetScrollTop,
            actualScrollTop: finalScroll,
            success: Math.abs(finalScroll - targetScrollTop) < 10,
            difference: Math.abs(finalScroll - targetScrollTop)
          });
        }, 50);
      };

      // Use requestAnimationFrame for better timing with DOM updates
      requestAnimationFrame(() => {
        console.log("🔍 SCROLL DEBUG - requestAnimationFrame callback executed");
        setTimeout(() => restoreScroll(1), 10);
        setTimeout(() => restoreScroll(2), 100);
        setTimeout(() => restoreScroll(3), 300);
      });
    } else {
      console.log("🔍 SCROLL DEBUG - Scroll restoration skipped:", {
        preserveScroll: preserveScroll,
        currentScrollTop: currentScrollTop,
        reason: !preserveScroll ? "preserveScroll is false" : "currentScrollTop is 0"
      });
    }
  }, [pageSize]);

  // Intersection Observer callback with improved logic
  const lastElementObserver = useCallback((node) => {
    if (loadingRef.current) return;
    
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    observerRef.current = new IntersectionObserver(
      (entries) => {
        console.log("👁️ UNIFIED INTERSECTION - Observer triggered:", {
          isIntersecting: entries[0].isIntersecting,
          hasMore,
          loading: loadingRef.current,
          remainingSearchDataLength: remainingSearchData.length,
          dataLength: data.length
        });
        
        if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
          console.log("👁️ UNIFIED INTERSECTION - Triggering loadMore...");
          loadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px', // Reduced from 100px to prevent premature loading
      }
    );
    
    if (node) {
      observerRef.current.observe(node);
    }
  }, [hasMore, loadMore, remainingSearchData.length, data.length]);

  // Set up the last element ref
  useEffect(() => {
    lastElementRef.current = lastElementObserver;
  }, [lastElementObserver]);

  // Return the ref function directly
  const refCallback = useCallback((node) => {
    console.log("🔗 UNIFIED REF CALLBACK - Node:", node ? "attached" : "detached");
    if (node) {
      console.log("🔗 UNIFIED REF CALLBACK - Setting up intersection observer...");
      lastElementObserver(node);
    }
  }, [lastElementObserver]);

  // Reset when dependencies change and trigger initial load
  useEffect(() => {
    if (dependencies.length > 0) {
      console.log("🔄 UNIFIED DEPENDENCIES - Dependencies changed, resetting...");
      reset();
      // Trigger initial load after reset
      setTimeout(() => {
        if (enabled && !loadingRef.current) {
          console.log("🔄 UNIFIED DEPENDENCIES - Triggering loadMore after reset...");
          loadMore();
        }
      }, 100);
    }
  }, dependencies);

  // Initial load
  useEffect(() => {
    if (enabled && data.length === 0 && !loadingRef.current && !isInitializedRef.current) {
      console.log("🚀 UNIFIED INITIAL LOAD - Triggering initial load...");
      isInitializedRef.current = true;
      loadMore();
    }
  }, [enabled, data.length, loadMore]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Add scroll event listener for debugging
  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      if (scrollDebugRef.current) {
        clearTimeout(scrollDebugRef.current);
      }
      scrollDebugRef.current = setTimeout(() => {
        console.log("🔍 SCROLL DEBUG - Scroll event detected:", {
          scrollTop: currentScroll,
          timestamp: new Date().toISOString()
        });
      }, 200); // Debounce scroll logging
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollDebugRef.current) {
        clearTimeout(scrollDebugRef.current);
      }
    };
  }, []);

  return {
    data,
    loading,
    hasMore,
    loadMore,
    reset,
    loadInitial,
    setDataDirectly,
    error,
    totalCount,
    lastElementRef: refCallback,
  };
};

export default useUnifiedInfiniteScroll;
